/**
 * useRouteOverviewColumns — saved column settings ⇄ TableCmp headCells for the Route Overview table,
 * plus the inline column-manager draft state (open / change / cancel / save).
 *
 * Port of WALHQ's generic useTableColumns, bound to the portal's basic.slice slot and the shared
 * table-column-settings endpoint. Saved settings are merged over the defaults by id: order and
 * visibility come from the saved row, everything else (preview renderer, widths) from the default;
 * unknown ids are dropped and new defaults are appended so a shipped column never disappears.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setOrderRouteOverviewColumns } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { updateOrderTableColumnSettings } from 'src/services/OrderService';

import {
    CP_ROUTE_OVERVIEW_TABLE_ALIAS,
    IRouteOverviewColumn,
    IRouteOverviewPreviewContext,
    LEG_OVERVIEW_COLUMN_CLASSNAME_MAP,
    ROUTE_OVERVIEW_COLUMN_TRANSLATION_MAP,
    ROUTE_OVERVIEW_DEFAULT_COLUMNS,
} from './legOverview.const';

export interface IRouteOverviewHeadCell {
    id: string;
    name?: string | React.JSX.Element;
    visible?: boolean;
    sortable?: boolean;
    rowClassName?: string;
    displayOrder?: number;
}

interface IProps {
    /** Rows currently shown — the first one feeds the manager's preview cards. */
    rows: any[];
    /** Head cells that are not user-manageable and always come first (the select-all checkbox). */
    pinnedFirstColumns?: IRouteOverviewHeadCell[];
    previewContext: IRouteOverviewPreviewContext;
    isDisabled?: boolean;
}

const sortByDisplayOrder = <T extends { displayOrder?: number }>(columns: T[]): T[] => [...columns].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

/** Only what the backend stores — never the JSX preview renderer or the preview row. */
const toPersisted = (column: any) => ({ id: column.id, name: column.name, visible: column.visible !== false, displayOrder: column.displayOrder });

const useRouteOverviewColumns = ({ rows, pinnedFirstColumns = [], previewContext, isDisabled = false }: IProps) => {
    const { t } = useTranslation();
    // The generated i18n key union makes t()'s return type unwieldy for a plain header label.
    const translate = t as (key: string) => string;
    const dispatch = useAppDispatch();
    const savedColumns = useAppSelector((state) => state.basic.orderRouteOverviewColumns);

    const [isEditing, setIsEditing] = useState(false);
    const [draftColumns, setDraftColumns] = useState<any[]>([]);

    const columns = useMemo((): IRouteOverviewColumn[] => {
        const defaultById = new Map(ROUTE_OVERVIEW_DEFAULT_COLUMNS.map((column) => [column.id, column]));
        const saved = (Array.isArray(savedColumns) ? savedColumns : []).filter((column: any) => defaultById.has(column.id));

        if (!saved.length) return sortByDisplayOrder(ROUTE_OVERVIEW_DEFAULT_COLUMNS);

        const ordered = sortByDisplayOrder(saved).map((column: any, index: number) => ({
            ...(defaultById.get(column.id) as IRouteOverviewColumn),
            visible: column.visible !== false,
            displayOrder: index + 1,
        }));
        const savedIds = new Set(ordered.map((column) => column.id));
        const added = ROUTE_OVERVIEW_DEFAULT_COLUMNS.filter((column) => !savedIds.has(column.id)).map((column, index) => ({ ...column, displayOrder: ordered.length + index + 1 }));

        return [...ordered, ...added];
    }, [savedColumns]);

    const resolveName = useCallback(
        (column: { id: string; name?: string }): string => (ROUTE_OVERVIEW_COLUMN_TRANSLATION_MAP[column.id] ? translate(ROUTE_OVERVIEW_COLUMN_TRANSLATION_MAP[column.id]) : column.name || column.id),
        [translate]
    );

    const headCells = useMemo(
        (): IRouteOverviewHeadCell[] => [
            ...pinnedFirstColumns,
            ...columns.map((column) => ({
                id: column.id,
                name: resolveName(column),
                visible: column.visible !== false,
                sortable: false,
                rowClassName: LEG_OVERVIEW_COLUMN_CLASSNAME_MAP[column.id] || '',
                displayOrder: column.displayOrder,
            })),
        ],
        [columns, pinnedFirstColumns, resolveName]
    );

    // DraggableLayout expects rows-of-cells: one preview row whose cells carry the column + the sample leg.
    const sortableData = useMemo(() => {
        if (!isEditing) return [];
        const row = rows[0] ?? null;

        return [columns.map((column) => ({ ...column, name: resolveName(column), Name: resolveName(column), boxWrapClassName: '', data: { column, row, context: previewContext } }))];
    }, [isEditing, columns, rows, previewContext, resolveName]);

    const hasChanges = useMemo(() => {
        if (!draftColumns.length) return false;

        return draftColumns.some((column) => {
            const original = columns.find((item) => item.id === column.id);

            return !original || column.displayOrder !== original.displayOrder || (column.visible !== false) !== (original.visible !== false);
        });
    }, [draftColumns, columns]);

    const openEditor = useCallback(() => setIsEditing(true), []);
    const closeEditor = useCallback(() => {
        setIsEditing(false);
        setDraftColumns([]);
    }, []);
    const onColumnChange = useCallback((updated: any[]) => setDraftColumns(updated), []);

    const saveColumns = useCallback(() => {
        const cleaned = sortByDisplayOrder(draftColumns).map(toPersisted);
        // Optimistic: the table re-renders from redux immediately; the save is best-effort (the orders
        // list follows the same pattern) — a failed write only means the next login shows the old layout.
        dispatch(setOrderRouteOverviewColumns(cleaned));
        setIsEditing(false);
        setDraftColumns([]);
        updateOrderTableColumnSettings({ tableAlias: CP_ROUTE_OVERVIEW_TABLE_ALIAS, columnSettings: cleaned }).catch((error: any) => {
            console.error('useRouteOverviewColumns: could not persist column settings', { alias: CP_ROUTE_OVERVIEW_TABLE_ALIAS, error: error?.message });
        });
    }, [draftColumns, dispatch]);

    return {
        columns,
        headCells,
        isEditing,
        hasChanges,
        columnsButtonProps: { isEditing, hasChanges, onOpen: openEditor, onCancel: closeEditor, onSave: saveColumns, isDisabled: isDisabled || rows.length === 0 },
        sortableData,
        onColumnChange,
    };
};

export default useRouteOverviewColumns;
